import { Plus, ShieldAlert } from 'lucide-react';
import { Button } from 'antd';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { useCanManageService } from '../../../hooks/use-can-manage-service';
import { ServiceRecordModal } from '../components/ServiceRecordModal';
import { ServiceRecordsTable } from '../components/ServiceRecordsTable';
import { useServiceRecords } from '../hooks/use-service-records';

/**
 * The Service records tab of a truck's own page: every service entered for it, newest first, and the place to add one (also how the starting
 * values are entered at setup). Settings (intervals, exclusions) are on the Service plan tab.
 */
export function TruckServiceRecordsTab({ truckId }: { truckId: string }) {
  const records = useServiceRecords(truckId);
  const canManage = useCanManageService();
  const [isAdding, setIsAdding] = useState(false);

  if (records.state.status === 'forbidden') return <Navigate to="/" replace />;
  if (records.state.status === 'loading') return <TableSkeleton label="Loading service records" withSearch />;
  if (records.state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the service records" description="Something went wrong loading them. Try reloading the page." />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-muted">The history of services done on this truck. A correction is a new entry, and the newest one counts.</p>
        {canManage && (
          <Button
            type="primary"
            icon={<Plus className="size-4" />}
            onClick={() => {
              records.resetSaveError();
              setIsAdding(true);
            }}
          >
            Add entry
          </Button>
        )}
      </div>
      <ServiceRecordsTable
        items={records.state.page.items}
        total={records.state.page.total}
        serviceTypes={records.serviceTypes}
        list={records.list}
        isRefreshing={records.isRefreshing}
      />
      <ServiceRecordModal
        open={isAdding}
        serviceTypes={records.serviceTypes}
        error={records.saveError}
        submitting={records.saving}
        onSubmit={records.addEntry}
        onClose={() => setIsAdding(false)}
      />
    </div>
  );
}
